import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useRationing } from '../../context/RationingContext';
import {
  Bars3Icon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import HoorayLogo from '../../assets/Hooray-Assets/Logo/hooray-for-power-logo.svg';
import Button from '../common/Button';
import { canAccessMonthlyFixedChargeWriteOff } from '../../utils/monthlyFixedChargeAdmin';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isAuthenticated, logout, user, hasRole, unit } = useAuth();
  const { rationingInfo, isRationingActive } = useRationing();
  const [isMounted, setIsMounted] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, [unit]);

  // Check if user has admin role
  const isAdmin = isAuthenticated() && (hasRole('ADMIN') || hasRole('MANAGEMENT') || hasRole('OPERATOR'));
  // Check if user can access site management
  const canManageSites = isAuthenticated() && (hasRole('ADMIN') || hasRole('MANAGEMENT'));
  const canWriteOffMonthlyFixedCharges = isAuthenticated() && canAccessMonthlyFixedChargeWriteOff(user);

  // Check if user has a prepaid unit
  const isPrepaidUser = unit?.isPrepaid === true;

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  const handleLogout = () => {
    try {
      logout();
      closeMenu();
      setTimeout(() => {
        navigate('/', { replace: true });
        window.location.reload();
      }, 100);
    } catch (error) {
      // console.error('Error during logout:', error);
      navigate('/', { replace: true });
      window.location.reload();
    }
  };

  // Navigation link style helpers
  const getDesktopNavLinkClass = ({ isActive }) => 
    `inline-flex items-center px-6 py-2 text-base font-medium tracking-wide font-mont ${
      isActive
        ? 'text-hooray-yellow font-semibold'
        : 'text-white/90 hover:text-white transition-colors duration-200'
    }`;

  const getMobileNavLinkClass = ({ isActive }) => 
    `block w-full px-5 py-3 text-base font-medium font-mont ${
      isActive
        ? 'text-hooray-yellow bg-white/10'
        : 'text-white/90 hover:text-white hover:bg-white/5'
    }`;

  // Components for different navigation sections
  const RegularUserNavLinks = ({ isMobile = false }) => {
    const linkClass = isMobile ? getMobileNavLinkClass : getDesktopNavLinkClass;
    const clickHandler = isMobile ? closeMenu : undefined;
    const showBillingLink = isPrepaidUser;

    return (
      <>
        <NavLink to="/dashboard" className={linkClass} onClick={clickHandler}>
          Dashboard
        </NavLink>
        {showBillingLink && (
          <NavLink to="/billing" className={linkClass} onClick={clickHandler}>
            Billing
          </NavLink>
        )}
        <NavLink to="/electricity-data" className={linkClass} onClick={clickHandler}>
          Electricity Data
        </NavLink>
        <div className="relative">
          <NavLink to="/rationing" className={linkClass} onClick={clickHandler}>
            Rationing
            {isRationingActive && (
              <span className="absolute -top-1 -right-2 px-1.5 py-0.5 text-xs font-medium rounded-full bg-danger text-white">
                !
              </span>
            )}
          </NavLink>
        </div>
      </>
    );
  };

  const AdminNavLinks = ({ isMobile = false }) => {
    const linkClass = isMobile ? getMobileNavLinkClass : getDesktopNavLinkClass;
    const clickHandler = isMobile ? closeMenu : undefined;

    return (
      <>
        <NavLink to="/admin" className={linkClass} onClick={clickHandler}>
          Admin Dashboard
        </NavLink>
        <NavLink to="/user-management" className={linkClass} onClick={clickHandler}>
          User Management
        </NavLink>
        {canManageSites && (
          <NavLink to="/site-management" className={linkClass} onClick={clickHandler}>
            Site Management
          </NavLink>
        )}
        {canWriteOffMonthlyFixedCharges && (
          <NavLink to="/admin/monthly-fixed-charge-write-offs" className={linkClass} onClick={clickHandler}>
            Fixed Charge Write-Offs
          </NavLink>
        )}
      </>
    );
  };

  const AuthButtons = ({ isMobile = false }) => {
    if (isAuthenticated()) {
      return (
        <Button
          variant="ctaSecondary"
          onClick={handleLogout}
          className={`${isMobile ? "w-full justify-center" : "min-w-[150px] justify-center"}`}
        >
          Logout
        </Button>
      );
    }
    
    return (
      <>
        <Button
          variant="ctaSecondary"
          to="/login"
          className={`${isMobile ? "w-full justify-center mb-2" : "min-w-[150px] justify-center"}`}
        >
          Login
        </Button>
        <Button
          variant="ctaSecondary"
          to="/register"
          className={`${isMobile ? "w-full justify-center" : "min-w-[150px] justify-center ml-4"}`}
        >
          Register
        </Button>
      </>
    );
  };

  if (!isMounted) return null;

  return (
    <header className="bg-hooray-red sticky top-0 z-50">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" aria-label="Main navigation">
        <div className="flex justify-between items-center h-20">
          <div className="flex-shrink-0">
            <Link to="/" className="block">
              <img 
                src={HoorayLogo} 
                alt="Hooray Power" 
                className="h-16 w-auto"
              />
            </Link>
          </div>

          {/* Desktop navigation links */}
          <div className="hidden md:flex md:items-center md:space-x-2">
            {isAuthenticated() ? (
              <div className="flex space-x-2">
                {isAdmin ? <AdminNavLinks /> : <RegularUserNavLinks />}
              </div>
            ) : (
              <div className="flex space-x-2">
                {/* <PublicNavLinks /> Removed */}
              </div>
            )}
            <div className="flex items-center ml-6">
              <AuthButtons />
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center md:hidden">
            <button
              type="button"
              className="inline-flex items-center justify-center p-2 rounded-md text-white hover:text-white/80 hover:bg-white/10 focus:outline-none"
              aria-expanded={isMenuOpen}
              onClick={toggleMenu}
            >
              <span className="sr-only">{isMenuOpen ? 'Close menu' : 'Open menu'}</span>
              {isMenuOpen ? (
                <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-hooray-red border-t border-white/10">
          <div className="py-2">
            {isAuthenticated() ? (
              isAdmin ? <AdminNavLinks isMobile /> : <RegularUserNavLinks isMobile />
            ) : (
              <></>
            )}
            <div className="mt-2 border-t border-white/10 p-4 space-y-2">
              <AuthButtons isMobile />
            </div>
          </div>
        </div>
      )}

      {/* Rationing alert banner */}
      {isRationingActive && rationingInfo && (
        <div className="bg-hooray-yellow text-hooray-red px-4 py-2 text-center text-sm font-medium">
          <span className="mr-2">⚡</span>
          Energy rationing is active: {rationingInfo.message}
        </div>
      )}
    </header>
  );
};

export default Header; 